import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { Bot, Shield, TrendingUp, Settings, Blocks, Globe, DollarSign, Key, Star, ArrowRight, Zap } from 'lucide-react';

const SectionContainer = styled.section`
  padding: 120px 0;
  background: #474545;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;

  @media (max-width: 768px) {
    text-align: left;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #FAECEC 0%, #0097B2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2rem;
    text-align: left;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const FeatureCard = styled(motion.div)`
  background: linear-gradient(135deg, rgba(0, 151, 178, 0.1) 0%, rgba(250, 236, 236, 0.05) 100%);
  border: 1px solid rgba(0, 151, 178, 0.2);
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    text-align: left;
  }

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 151, 178, 0.2);
    border-color: rgba(0, 151, 178, 0.4);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #0097B2 0%, #007a94 100%);
  }

  &::after {
    content: '';
    position: absolute;
    left: -1px;
    top: -1px;
    right: -1px;
    bottom: -1px;
    opacity: 0;
    transform: scale(1, 0.2);
    transition: all 500ms ease;
    background: linear-gradient(to top, #0097B2 0%, #FAECEC 100%);
    z-index: -1;
    border-radius: 20px;
  }

  &:hover::after {
    opacity: 0.1;
    transform: scale(1, 1);
  }
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #0097B2 0%, #007a94 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  box-shadow: 0 8px 25px rgba(0, 151, 178, 0.3);
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #FAECEC;
  margin-bottom: 16px;
`;

const FeatureDescription = styled.p`
  color: #FAECEC;
  opacity: 0.8;
  line-height: 1.6;
  font-size: 1rem;
`;

const Badge = styled.span`
  position: absolute;
  top: 20px;
  right: 20px;
  background: linear-gradient(135deg, #0097B2 0%, #007a94 100%);
  color: #FAECEC;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
`;

/* No Commission Highlight Section */
const HighlightSection = styled.div`
  margin-top: 80px;
  background: linear-gradient(135deg, rgba(0, 151, 178, 0.15) 0%, rgba(250, 236, 236, 0.08) 100%);
  border: 2px solid rgba(0, 151, 178, 0.3);
  border-radius: 24px;
  padding: 50px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 30px 20px;
    text-align: left;
  }
`;

const HighlightTitle = styled(motion.h3)`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #0097B2 0%, #FAECEC 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const HighlightDescription = styled.p`
  color: #FAECEC;
  font-size: 1.2rem;
  line-height: 1.7;
  max-width: 800px;
  margin: 0 auto 30px;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin: 0 0 30px;
  }
`;

const HighlightFeatures = styled.div`
  display: flex;
  justify-content: center;
  gap: 40px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const HighlightItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #FAECEC;
  font-size: 1.1rem;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const HighlightIcon = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #0097B2 0%, #007a94 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(0, 151, 178, 0.3);
`;

/* Agents Section Header */
const AgentsSectionHeader = styled.div`
  margin-top: 80px;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  gap: 20px;
`;

const AgentsSectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(135deg, #FAECEC 0%, #0097B2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const AgentsSectionLine = styled.div`
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, rgba(0, 151, 178, 0.4), transparent);
`;

/* Consensus Agent Block */
const ConsensusBlock = styled(motion.div)`
  margin-top: 80px;
  background: linear-gradient(135deg, #0d1117 0%, #111827 100%);
  border: 1px solid rgba(245, 158, 11, 0.25);
  border-radius: 28px;
  padding: 0;
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #f59e0b 0%, #0097B2 50%, #f59e0b 100%);
  }
`;

const ConsensusInner = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ConsensusTextSide = styled.div`
  padding: 56px 52px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 40px 28px;
  }
`;

const ConsensusImageSide = styled.div`
  background: #0d1117;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-left: 1px solid rgba(245, 158, 11, 0.12);
  min-height: 360px;

  @media (max-width: 900px) {
    border-left: none;
    border-top: 1px solid rgba(245, 158, 11, 0.12);
    min-height: 280px;
  }
`;

const ConsensusImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.92;
  transition: transform 0.5s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const ConsensusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: #000;
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  margin-bottom: 20px;
  width: fit-content;
`;

const ConsensusTitle = styled.h3`
  font-size: 2rem;
  font-weight: 800;
  color: #FAECEC;
  margin-bottom: 16px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const ConsensusDesc = styled.p`
  font-size: 1.05rem;
  color: rgba(250, 236, 236, 0.75);
  line-height: 1.7;
  margin-bottom: 28px;
`;

const AiModelChips = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 32px;
`;

const ModelChip = styled.span`
  background: rgba(0, 151, 178, 0.12);
  border: 1px solid rgba(0, 151, 178, 0.3);
  color: rgba(250, 236, 236, 0.85);
  padding: 5px 12px;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 500;
  font-family: 'Courier New', monospace;
`;

const ConsensusLearnMore = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.1) 100%);
  border: 1px solid rgba(245, 158, 11, 0.4);
  color: #f59e0b;
  padding: 12px 24px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: all 0.3s ease;
  width: fit-content;

  &:hover {
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.25) 0%, rgba(217, 119, 6, 0.2) 100%);
    border-color: rgba(245, 158, 11, 0.7);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(245, 158, 11, 0.2);
    color: #f59e0b;
  }
`;

/* Trading Team Block */
const TradingTeamBlock = styled(motion.div)`
  margin-top: 40px;
  background: linear-gradient(135deg, #0d1117 0%, #111827 100%);
  border: 1px solid rgba(0, 151, 178, 0.25);
  border-radius: 28px;
  padding: 0;
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #0097B2 0%, #00d4ff 50%, #0097B2 100%);
  }
`;

const TradingTeamBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #0097B2 0%, #007a94 100%);
  color: #FAECEC;
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  margin-bottom: 20px;
  width: fit-content;
`;

const TradingTeamGetStarted = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, rgba(0, 151, 178, 0.15) 0%, rgba(0, 122, 148, 0.1) 100%);
  border: 1px solid rgba(0, 151, 178, 0.4);
  color: #0097B2;
  padding: 12px 24px;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: all 0.3s ease;
  width: fit-content;

  &:hover {
    background: linear-gradient(135deg, rgba(0, 151, 178, 0.25) 0%, rgba(0, 122, 148, 0.2) 100%);
    border-color: rgba(0, 151, 178, 0.7);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 151, 178, 0.2);
    color: #0097B2;
  }
`;

const FeaturesSection = () => {
  const features = [
    {
      icon: <Bot size={32} color="#FAECEC" />,
      title: "AI Bot Chat Builder with LLMs",
      description: "Revolutionary AI-powered bot creation using the latest LLMs (Claude, OpenAI, xAI, Gemini, DeepSeek). Create sophisticated trading strategies in minutes, not hours.",
      badge: "NEW"
    },
    {
      icon: <Shield size={32} color="#FAECEC" />,
      title: "Secure API Key Management",
      description: "Bank-level encryption for your exchange API keys. We never store your funds or have withdrawal access - you stay in complete control.",
      badge: null
    },
    {
      icon: <TrendingUp size={32} color="#FAECEC" />,
      title: "f01i AI Agent",
      description: "Get a complete technical analysis of any asset just in a minute.",
      badge: null
    },
    {
      icon: <Settings size={32} color="#FAECEC" />,
      title: "Manual Bot Settings for Pros",
      description: "Advanced users can fine-tune every parameter. Complete control over order types, risk management, and execution strategies.",
      badge: null
    },
    {
      icon: <Blocks size={32} color="#FAECEC" />,
      title: "f01i Blocks",
      description: "Create comprehensive custom AI trading strategies using powerful tools like Coinglass, CoinMarketCap, CoinGecko, and more.",
      badge: "NEW"
    },
    {
      icon: <Globe size={32} color="#FAECEC" />,
      title: "Multi-Exchange Support",
      description: "Currently we're supporting Bybit, Binance, Crypto.com, OKX, MEXC, Coinbase and Hyperliquid. Trade across multiple platforms seamlessly.",
      badge: null
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <SectionContainer id="features">
      <Container>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Powerful Features
          </SectionTitle>
        </SectionHeader>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
              >
                {feature.badge && <Badge>{feature.badge}</Badge>}
                <IconWrapper>
                  {feature.icon}
                </IconWrapper>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </motion.div>

        {/* Agents Section Header */}
        <AgentsSectionHeader>
          <AgentsSectionTitle>Agents</AgentsSectionTitle>
          <AgentsSectionLine />
        </AgentsSectionHeader>

        {/* Consensus Agent Block */}
        <ConsensusBlock
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <ConsensusInner>
            <ConsensusTextSide>
              <ConsensusBadge>
                <Star size={11} fill="#000" />
                Pro Plans Only
              </ConsensusBadge>
              <ConsensusTitle>f01i Consensus Agent</ConsensusTitle>
              <ConsensusDesc>
                The most advanced f01i Agent. 4 top AI models compete analyzing real-time market data. 
                The Consensus Agent judges their recommendations, analyzes patterns and makes the final 
                autonomous trading decision.
              </ConsensusDesc>
              <AiModelChips>
                <ModelChip>Claude (Anthropic)</ModelChip>
                <ModelChip>Gemini (Google)</ModelChip>
                <ModelChip>GPT (OpenAI)</ModelChip>
                <ModelChip>Grok (xAI)</ModelChip>
              </AiModelChips>
              <ConsensusLearnMore to="/consensusagent">
                Learn More
                <ArrowRight size={16} />
              </ConsensusLearnMore>
            </ConsensusTextSide>
            <ConsensusImageSide>
              <ConsensusImg
                src="/consensus_agent.webp"
                alt="f01i Consensus Agent — 4 AI models analyzing market data"
                loading="lazy"
              />
            </ConsensusImageSide>
          </ConsensusInner>
        </ConsensusBlock>

        {/* Trading Team Block */}
        <TradingTeamBlock
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <ConsensusInner>
            <ConsensusTextSide>
              <TradingTeamBadge>
                <Zap size={11} />
                NEW
              </TradingTeamBadge>
              <ConsensusTitle>f01i Trading Team</ConsensusTitle>
              <ConsensusDesc>
                Your personal AI trading team (like Teamly or OpenClaw), but exclusively for crypto 
                investments and trading. Agents make research, open positions, and communicate with 
                you via Telegram. Add the required API keys.
              </ConsensusDesc>
              <AiModelChips>
                <ModelChip>Telegram Bot</ModelChip>
                <ModelChip>Research Agents</ModelChip>
                <ModelChip>Auto Positions</ModelChip>
              </AiModelChips>
              <TradingTeamGetStarted
                href="https://f01i.app/login"
                target="_blank"
                rel="noopener noreferrer"
              >
                Get Started
                <ArrowRight size={16} />
              </TradingTeamGetStarted>
            </ConsensusTextSide>
            <ConsensusImageSide>
              <ConsensusImg
                src="/trading_team.png"
                alt="f01i Trading Team — AI agents for crypto trading via Telegram"
                loading="lazy"
              />
            </ConsensusImageSide>
          </ConsensusInner>
        </TradingTeamBlock>

        {/* No Commission Highlight Block */}
        <HighlightSection>
          <HighlightTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Zero Trading Commissions
          </HighlightTitle>
          <HighlightDescription>
            Unlike other platforms, f01i.ai charges no commissions on your trades. 
            You only pay for bot executions — keep more of your profits and trade with complete transparency.
          </HighlightDescription>
          <HighlightFeatures>
            <HighlightItem>
              <HighlightIcon>
                <DollarSign size={24} color="#FAECEC" />
              </HighlightIcon>
              <span>No trading fees — ever</span>
            </HighlightItem>
            <HighlightItem>
              <HighlightIcon>
                <Key size={24} color="#FAECEC" />
              </HighlightIcon>
              <span>Use your own LLM API keys</span>
            </HighlightItem>
          </HighlightFeatures>
        </HighlightSection>
      </Container>
    </SectionContainer>
  );
};

export default FeaturesSection;
